import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components';
import Background from '../components/Background';
import PlanetSpeaker from '../components/PlanetSpeaker';
import { openReadingStream, startFollowup, getSession, releaseStreamLock } from '../services/api';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';

// Message type
type Message = {
  agent: string;
  role: string;
  content: string;
  displayAgent?: string; // The agent to show in UI (Aivaris if no planet match)
  originalAgent?: string; // Original agent name (for non-planet agents)
};

// Planet colors for cards
const planetColors: Record<string, string> = {
  'Sun': '#FFE5A0',
  'Moon': '#E8E4F0',
  'Mercury': '#B8E6F5',
  'Venus': '#FFD4E5',
  'Mars': '#FF9A8B',
  'Jupiter': '#C9B5E8',
  'Saturn': '#9B7BC8',
  'Uranus': '#7FE8E8',
  'Neptune': '#9BB5FF',
  'Moderator': '#D4AF37', // Gold for moderator
  'Aivaris': '#D4AF37'
};

const Reading: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const day = (searchParams.get('day') || 'today') as 'today' | 'tomorrow';
  const typeParam = (searchParams.get('type') || 'daily') as 'daily' | 'custom';
  const customQuestion = searchParams.get('question') || '';
  const modeParam = (searchParams.get('mode') as 'western' | 'vedic' | null);
  const astrologyMode = (modeParam || (localStorage.getItem('astrology_mode') as 'western' | 'vedic') || 'western') as 'western' | 'vedic';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [completed, setCompleted] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activeAgent, setActiveAgent] = useState<string | null>(null);

  const [userQuestion, setUserQuestion] = useState<string>('');
  const questionInputRef = useRef<HTMLInputElement | null>(null);
  const { user: authUser } = useAuth();
  const displayName = (authUser?.user_metadata as any)?.full_name || authUser?.email?.split('@')[0] || 'Seeker';

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  const activeRequestIdRef = useRef<string | null>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]); // Refs for each message card

  // Initial setup
  useEffect(() => {
    const questionParam = searchParams.get('question');
    if (typeParam === 'custom' && customQuestion) {
      setUserQuestion(customQuestion);
    } else if (questionParam) {
      setUserQuestion(questionParam);
    } else if (typeParam === 'daily') {
      const questionText = day === 'today' ? 'How is my day today?' : 'How is my day tomorrow?';
      setUserQuestion(questionText);
    }
  }, [typeParam, customQuestion, day, searchParams]);

  // Auto-start for daily readings
  useEffect(() => {
    if (typeParam === 'daily' && !sessionId && !loading && !isConnectingRef.current) {
      handleUserQuestion();
    }
  }, [typeParam, sessionId, loading]);

  // Scroll Spy Logic - Improved
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -70% 0px', // Top area where we detect active message
      threshold: [0, 0.1, 0.5, 1]
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      const visibleEntries = entries.filter(entry => entry.isIntersecting);

      if (visibleEntries.length > 0) {
        visibleEntries.sort((a, b) => {
          // Prioritize entries that are more visible
          if (a.intersectionRatio !== b.intersectionRatio) {
            return b.intersectionRatio - a.intersectionRatio;
          }
          // Then by position
          return a.boundingClientRect.top - b.boundingClientRect.top;
        });

        const topEntry = visibleEntries[0];
        const index = Number(topEntry.target.getAttribute('data-index'));

        if (!isNaN(index) && messages[index]) {
          const msg = messages[index];
          if (msg.role !== 'user') {
            // Clean agent name to match PlanetSpeaker component (remove ' Agent' suffix)
            const cleanAgentName = msg.agent.replace(' Agent', '').replace(' AGENT', '');
            setActiveAgent(cleanAgentName);
          }
        }
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    messageRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [messages]);


  // Smart Scroll Handling
  const scrollToBottom = useCallback(() => {
    if (!messagesEndRef.current) return;

    // Check if user is already near bottom (within 300px)
    const isNearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 500;

    // Only auto-scroll if we are near the bottom OR if it's the very first message
    if (isNearBottom || messages.length <= 1) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length]);

  // Trigger scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages.length, scrollToBottom]);


  const attachStreamListeners = (es: EventSource) => {
    const processedMessages = new Set<string>();

    es.addEventListener('session', (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data);
        if (data?.session_id) setSessionId(data.session_id);
      } catch (e) { }
    });

    es.addEventListener('message', (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data);
        if (!data?.agent) return;

        // Log empty content for debugging but still process the message
        if (!data?.content) {
          logger.warn('Reading', 'Received message with empty content', { agent: data.agent });
        }

        const messageId = `${data.agent}-${data.content?.substring(0, 20) || 'empty'}`;
        if (processedMessages.has(messageId)) return;
        processedMessages.add(messageId);

        const newMessage: Message = {
          agent: data.agent,
          role: data.role || 'planet',
          content: data.content || ''
        };

        setMessages(prev => [...prev, newMessage]);

        // If it's the first message of a response, verify we scroll to it
        if (messages.length > 0 && messages[messages.length - 1].role === 'user') {
          // Force scroll to this new answer
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }, 100);
        }

      } catch (e) { }
    });

    es.addEventListener('complete', () => {
      setCompleted(true);
      setLoading(false);
      es.close();
      isConnectingRef.current = false;
    });

    es.onerror = () => {
      es.close();

      // If we haven't received any messages yet, this is an error
      if (messages.length === 0 || (messages.length === 1 && messages[0].role === 'user')) {
        // If EventSource never opened (readyState 0 = CONNECTING), it was likely a 429 or auth error
        if (es.readyState === EventSource.CONNECTING) {
          setError('The stars are still aligning from a previous reading. Please wait a moment and try again.');
        } else {
          setError('Connection lost. Please try again.');
        }
        // Remove the user message we added optimistically if it failed immediately
        setMessages(prev => prev.filter(m => m.role !== 'user'));
      }

      setCompleted(true);
      setLoading(false);
      isConnectingRef.current = false;
    };
  };

  const handleUserQuestion = async () => {
    if (!userQuestion.trim()) return;

    const requestId = `req-${Date.now()}`;
    activeRequestIdRef.current = requestId;

    const question = userQuestion.trim();
    setUserQuestion('');
    setLoading(true);
    setCompleted(false);

    // Add user message
    const userMsg: Message = { agent: displayName, role: 'user', content: question };
    setMessages(prev => [...prev, userMsg]);

    // Force scroll to bottom when user asks question
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);

    try {
      logger.debug('Reading', 'Starting question flow', { question, sessionId, typeParam });

      if (eventSourceRef.current) eventSourceRef.current.close();

      // Release any stuck locks from previous interrupted streams
      await releaseStreamLock();

      let es: EventSource;
      isConnectingRef.current = true;

      if (sessionId) {
        // Follow-up
        logger.debug('Reading', 'Sending follow-up', { sessionId });
        es = await openReadingStream({ type: 'custom', question, session_id: sessionId, mode: astrologyMode });
      } else {
        // New reading
        // Check if the question is exactly the daily reading question
        const isDailyQuestion = !question || question.toLowerCase().trim() === `how is my day ${day}?`;

        if (typeParam === 'daily' && isDailyQuestion) {
          logger.debug('Reading', 'Starting daily reading', { day });
          es = await openReadingStream({ type: 'daily', day, mode: astrologyMode });
        } else {
          // Custom question start
          logger.debug('Reading', 'Starting custom question flow');
          const start = await startFollowup(question, 'custom');
          logger.debug('Reading', 'Custom session started', { sessionId: start.session_id, action: start.action });
          setSessionId(start.session_id);

          if (start.action === 'ask' && start.followup) {
            logger.debug('Reading', 'Moderator ask received', { followup: start.followup });
            const modMsg: Message = { agent: 'Moderator', role: 'planet', content: start.followup };
            setMessages(prev => [...prev, modMsg]);
            setLoading(false);
            isConnectingRef.current = false;
            return;
          }

          logger.debug('Reading', 'Opening stream for custom session', { sessionId: start.session_id });
          es = await openReadingStream({ type: 'custom', question, session_id: start.session_id, mode: astrologyMode });
        }
      }

      eventSourceRef.current = es;
      attachStreamListeners(es);
    } catch (e: any) {
      logger.error('Reading', 'Failed to start reading', e);
      setError(e.message || 'Failed to start reading');
      setLoading(false);
      isConnectingRef.current = false;
    }
  };

  const isInputLocked = !completed && (loading || isConnectingRef.current);

  // Helper to clean message content
  const cleanContent = (content: string) => {
    // Remove JSON blocks like {"intent":...}
    return content.replace(/\{"intent":[^}]+\}/g, '').trim();
  };

  return (
    <div className="min-h-screen relative bg-[#050508]">
      <Background />

      {/* Fixed Header & Planet Area - Full Width */}
      <div className="fixed top-0 left-0 right-0 z-30 pointer-events-none">
        {/* Content Container */}
        <div className="max-w-3xl mx-auto pointer-events-auto flex flex-col">
          {/* Planet Visualization - Moved to top */}
          <div className="w-full px-2 md:px-0 pt-4 md:pt-6">
            <PlanetSpeaker activeAgent={activeAgent} isSpeaking={!!activeAgent} />
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="relative z-10 min-h-screen flex flex-col max-w-3xl mx-auto pt-[180px] md:pt-[200px] pb-72 px-4 md:px-6">
        {messages.map((msg, idx) => {
          const isUser = msg.role === 'user';
          const color = planetColors[msg.agent] || '#D4AF37';
          let displayContent = cleanContent(msg.content);

          if (!displayContent) return null;

          // Parse Do's and Don'ts from content - improved regex for flexibility
          const dosDontsRegex = /(\*\*Do:?\*\*[^\n]+|\*\*Don'?t:?\*\*[^\n]+)/gi;
          const dosDonts = displayContent.match(dosDontsRegex) || [];
          const mainContent = displayContent.replace(dosDontsRegex, '').trim();

          return (
            <div
              key={idx}
              ref={el => { messageRefs.current[idx] = el; }}
              data-index={idx}
              className={`flex flex-col items-center animate-fade-in w-full transition-opacity duration-500 mb-16 md:mb-20`}
            >
              {/* Agent Label (only for planets) - More Visible */}
              {!isUser && (
                <div className="flex flex-col items-center mb-6">
                  <span className="text-xs font-heading tracking-[0.3em] uppercase text-white/50 mb-2">
                    Insight from
                  </span>
                  <span
                    className="text-sm font-heading tracking-[0.25em] uppercase font-semibold"
                    style={{ color: color }}
                  >
                    {msg.agent.replace(' Agent', '').replace(' AGENT', '')}
                  </span>
                </div>
              )}

              {/* The Card - Glass Grain Style with Glow */}
              <div
                className={`relative w-full max-w-2xl rounded-2xl backdrop-blur-xl transition-all duration-700 group overflow-hidden
                    ${isUser
                    ? 'p-6 md:p-8 glass-grain border border-white/5'
                    : 'p-8 md:p-12 glass-grain border border-white/10'
                  }`}
                style={!isUser ? {
                  background: `linear-gradient(145deg, rgba(20, 20, 32, 0.6) 0%, ${color}03 100%)`,
                  borderColor: `${color}15`,
                  boxShadow: `0 8px 32px rgba(0,0,0,0.2)`
                } : {
                  background: 'rgba(20, 20, 32, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (!isUser) {
                    e.currentTarget.style.borderColor = `${color}30`;
                    e.currentTarget.style.boxShadow = `0 10px 40px -10px ${color}15`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isUser) {
                    e.currentTarget.style.borderColor = `${color}15`;
                    e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,0.2)`;
                  }
                }}
              >
                {/* Background Glow - Like Landing Page */}
                {!isUser && (
                  <div
                    className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-[100px] opacity-20 pointer-events-none transition-opacity duration-700 group-hover:opacity-30"
                    style={{ backgroundColor: color }}
                  />
                )}

                <div className={`prose prose-invert max-w-none font-elegant leading-relaxed md:leading-loose relative z-10
                    ${isUser
                    ? 'text-base md:text-lg text-white/70 italic font-light text-center'
                    : 'text-lg md:text-xl text-text-secondary text-center'
                  }`}>
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => <p className="mb-5 md:mb-6 last:mb-0" {...props} />
                    }}
                  >
                    {mainContent}
                  </ReactMarkdown>

                  {/* Do's and Don'ts as inline cards */}
                  {dosDonts.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
                      {dosDonts.map((item, i) => {
                        // Check for "don't" first since "don't" contains "do"
                        const isDo = !item.toLowerCase().includes("don't") && item.toLowerCase().includes("do");
                        const text = item.replace(/\*\*Do:?\*\*\s*/i, '').replace(/\*\*Don'?t:?\*\*\s*/i, '').trim();
                        return (
                          <div
                            key={i}
                            className="glass-grain rounded-xl p-4 border border-white/5 text-left"
                            style={{
                              background: isDo ? `${color}05` : 'rgba(255, 100, 100, 0.03)',
                              borderColor: isDo ? `${color}20` : 'rgba(255, 100, 100, 0.15)'
                            }}
                          >
                            <div className="text-xs font-heading tracking-wider uppercase mb-2" style={{ color: isDo ? color : '#ff6b6b' }}>
                              {isDo ? 'Do' : "Don't"}
                            </div>
                            <div className="text-sm text-white/80 leading-relaxed">
                              {text}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} className="h-10" />
      </div>

      {/* Input Area - Floating above Bottom Nav */}
      <div className="fixed bottom-[80px] left-0 right-0 p-6 z-[60]">
        <div className="max-w-2xl mx-auto relative group">

          {/* Input Glow - Subtle */}
          <div className={`absolute -inset-1 bg-gradient-to-r from-transparent via-cosmic-gold/10 to-transparent rounded-full blur transition-opacity duration-1000 ${isInputLocked ? 'opacity-0' : 'opacity-100 group-hover:opacity-60'}`} />

          <div className="relative flex items-center">
            <input
              ref={questionInputRef}
              type="text"
              value={userQuestion}
              onChange={(e) => setUserQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isInputLocked && handleUserQuestion()}
              placeholder={isInputLocked ? "The stars are aligning..." : "Cast your query into the cosmos..."}
              disabled={isInputLocked}
              className="w-full glass-grain border border-white/10 rounded-full px-8 py-5 pr-16 text-white placeholder-white/20 focus:outline-none focus:border-cosmic-gold/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-elegant text-lg md:text-xl text-center tracking-wide shadow-2xl backdrop-blur-xl"
              style={{ background: 'rgba(20, 20, 32, 0.6)' }}
            />

            <button
              onClick={handleUserQuestion}
              disabled={isInputLocked || !userQuestion.trim()}
              className="absolute right-3 w-10 h-10 rounded-full bg-cosmic-gold/10 text-cosmic-gold border border-cosmic-gold/20 flex items-center justify-center hover:bg-cosmic-gold hover:text-[#050508] transition-all duration-500 disabled:opacity-0 disabled:scale-75"
            >
              <span className="text-xl">✧</span>
            </button>
          </div>

          <div className="text-center mt-4 h-6">
            {isInputLocked && (
              <span className="text-[10px] text-cosmic-gold/60 font-heading tracking-[0.2em] uppercase animate-pulse">
                Divining...
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reading;
